import { formatMoney, t } from "@hakili/i18n";
import { ConfirmDialog } from "./confirm-dialog.js";
import { type ReactElement, useState } from "react";

import type { DoorResult, PriceGapView } from "../session/useSession.js";

/**
 * `PRICE-GAP-DEALER`, M1 (WF-DLR-08, WF-CLI-14; the founder's ruling of 16 October 2026): **« Décision d'écart de
 * prix » , the seller answers the gap ON THE PLATFORM**, from the car's own page.
 *
 * Three answers, each one the dealer's own HTTP call: « M'aligner sur la valeur expertisée », « Proposer un autre
 * prix » (any price at or under value x 1,10 conforms) and « Maintenir mon prix ». The card knows the figures the
 * door read , the expertised value, the ceiling, the price that stands , and refuses a price above the ceiling or
 * not under the standing one BEFORE calling (the door refuses it again, in its own words; hiding is a courtesy).
 *
 * The price lock stays for « Modifier le prix » and the card says so in one sentence: these answers are the single
 * exception, and only downward.
 */
const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

/** `A3`, DEALER-CONFIRMS: the words each answer asks with, and the label of the button that confirms it. */
const CONFIRM_TITLE = {
  align: "dealer.confirmer.alignerTitre",
  propose: "dealer.confirmer.proposerTitre",
  maintain: "dealer.confirmer.maintenirTitre",
} as const;
const CONFIRM_BODY = {
  align: "dealer.confirmer.alignerCorps",
  propose: "dealer.confirmer.proposerCorps",
  maintain: "dealer.confirmer.maintenirCorps",
} as const;
const CONFIRM_LABEL = {
  align: "dealer.ecart.aligner",
  propose: "dealer.ecart.envoyer",
  maintain: "dealer.ecart.maintenir",
} as const;

export function PriceGapCard(props: {
  readonly view: PriceGapView;
  readonly answer: (
    answer:
      { readonly kind: "align" | "maintain" } | { readonly kind: "propose"; readonly priceAmount: number },
  ) => Promise<DoorResult<unknown>>;
  readonly onChanged?: () => void;
}): ReactElement {
  const { view } = props;
  const [busy, setBusy] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [amount, setAmount] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);

  /** `A3`, DEALER-CONFIRMS: the answer waiting for the dealer's « confirmer »; nothing is written until then. */
  const [asking, setAsking] = useState<
    | { readonly kind: "align" | "maintain" }
    | { readonly kind: "propose"; readonly priceAmount: number }
    | null
  >(null);

  const send = (
    answer:
      { readonly kind: "align" | "maintain" } | { readonly kind: "propose"; readonly priceAmount: number },
  ): void => {
    setBusy(true);
    setRefusal(null);
    void props.answer(answer).then((done) => {
      setBusy(false);
      if (done.ok) {
        setProposing(false);
        props.onChanged?.();
      } else setRefusal(done.message);
    });
  };

  const propose = (): void => {
    const price = Number(amount.replace(/\s/gu, ""));
    if (!Number.isInteger(price) || price <= 0) {
      setRefusal(t("dealer.ecart.refusMontant"));
      return;
    }
    if (price > view.ceiling) {
      setRefusal(fill("dealer.ecart.refusPlafond", { plafond: formatMoney(view.ceiling) }));
      return;
    }
    if (price >= view.currentPrice) {
      setRefusal(fill("dealer.ecart.refusBaisse", { prix: formatMoney(view.currentPrice) }));
      return;
    }
    setAsking({ kind: "propose", priceAmount: price });
  };

  const reply = view.answer;
  const replyKey =
    reply === null
      ? null
      : reply.decision === "aligne"
        ? "dealer.ecart.reponseAligne"
        : reply.decision === "propose"
          ? "dealer.ecart.reponsePropose"
          : "dealer.ecart.reponseMaintenu";
  // An answer given is not final while the gap stands: the seller may change his mind (the door allows it), except
  // after an alignment , the price already conforms and the gap is gone at the next read.
  const open = reply?.decision !== "aligne";

  return (
    <section
      className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4"
      data-testid="vehicule-ecart"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-gray-900">{t("dealer.ecart.titre")}</h3>
        <span className="text-xs text-gray-500">
          {fill("dealer.ecart.sous", { dossier: view.dossierReference })}
        </span>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-gray-700" data-testid="vehicule-ecart-explication">
        {fill("dealer.ecart.explication", {
          valeur: formatMoney(view.expertisedValue),
          prix: formatMoney(view.currentPrice),
          plafond: formatMoney(view.ceiling),
        })}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-gray-500">{t("dealer.ecart.valeur")}</dt>
        <dd className="text-right font-medium text-gray-900">{formatMoney(view.expertisedValue)}</dd>
        <dt className="text-gray-500">{t("dealer.ecart.plafond")}</dt>
        <dd className="text-right font-medium text-gray-900">{formatMoney(view.ceiling)}</dd>
        <dt className="text-gray-500">{t("dealer.ecart.prix")}</dt>
        <dd className="text-right font-medium text-gray-900">{formatMoney(view.currentPrice)}</dd>
      </dl>

      {replyKey === null || reply === null ? null : (
        <p
          className="mt-3 rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-gray-900"
          data-testid="vehicule-ecart-reponse"
        >
          {fill(replyKey, { prix: formatMoney(reply.price) })}
        </p>
      )}

      {open ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setAsking({ kind: "align" });
            }}
            data-testid="vehicule-ecart-aligner"
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {t("dealer.ecart.aligner")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setProposing(!proposing);
              setRefusal(null);
            }}
            data-testid="vehicule-ecart-proposer"
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700"
          >
            {t("dealer.ecart.proposer")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setAsking({ kind: "maintain" });
            }}
            data-testid="vehicule-ecart-maintenir"
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700"
          >
            {t("dealer.ecart.maintenir")}
          </button>
        </div>
      ) : null}

      {open && proposing ? (
        <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="vehicule-ecart-proposition">
          <label className="text-xs text-gray-600">
            {t("dealer.ecart.montant")}
            <input
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
              }}
              data-testid="vehicule-ecart-montant"
              className="mt-1 block w-44 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={propose}
            data-testid="vehicule-ecart-envoyer"
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {t("dealer.ecart.envoyer")}
          </button>
        </div>
      ) : null}

      {refusal === null ? null : (
        <p role="alert" data-testid="vehicule-ecart-refus" className="mt-2 text-sm text-rose-600">
          {refusal}
        </p>
      )}
      <p className="mt-3 text-xs leading-relaxed text-gray-500">{t("dealer.ecart.verrou")}</p>
      {asking === null ? null : (
        <ConfirmDialog
          title={t(CONFIRM_TITLE[asking.kind])}
          body={fill(
            CONFIRM_BODY[asking.kind],
            asking.kind === "maintain"
              ? { prix: formatMoney(view.currentPrice) }
              : {
                  ancien: formatMoney(view.currentPrice),
                  nouveau: formatMoney("priceAmount" in asking ? asking.priceAmount : view.expertisedValue),
                },
          )}
          confirmLabel={t(CONFIRM_LABEL[asking.kind])}
          busy={busy}
          onCancel={() => {
            setAsking(null);
          }}
          onConfirm={() => {
            const answer = asking;
            setAsking(null);
            send(answer);
          }}
        />
      )}
    </section>
  );
}
